

const IS_PROD = ("true" === 'true');
const ESIMGO_API_URL = IS_PROD ? "https://api.esim-go.com" : "https://staging-api.esim-go.com";
const API_VERSION = "/v2.3";
const ESIMS_PATHS = Object.freeze({
  Esims: "/esims",
  Customers: "/customer",
  Assignments: "/assignments",
  QR: "/qr",
  Details: "/details",
});
const TOKEN_PARAM = "?token=";

const ZENDESK_URL = "https://help-lufthansa.breezesim.com";
const ZENDESK_GUIDES_FALLBACK = "/categories/28864862528785-Device-Management";
const ZENDESK_GUIDES_CATEGORY = "28864862528785";
const ZENDESK_DEVICES_SECTION = "28864895603729-Compatible-Devices";

/**
 * Fetches a URL, retrying on network errors or non-OK responses with
 * exponential backoff. The account page's eSIM list fires many concurrent
 * requests to the eSIM-Go API and the helpers app on every load; without
 * retries, a single transient timeout/rate-limit drops that eSIM from the
 * count silently, making the total flicker between page loads.
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {number} [retries]     Number of retries after the first attempt.
 * @param {number} [baseDelayMs] Base delay for exponential backoff.
 * @returns {Promise<Response>}  Resolves to the last response/throws the last error.
 */
async function fetchWithRetry(url, options, retries = 2, baseDelayMs = 300) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, options);
      if (res.ok) return res;
      lastErr = new Error(`Request to ${url} failed: ${res.status}`);
    } catch (err) {
      lastErr = err;
    }
    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, baseDelayMs * Math.pow(2, attempt)));
    }
  }
  throw lastErr;
}

/**
 * Encrypts the given plain text with the provided key using AES-256-GCM algorithm.
 * @param {string} key - The encryption key to use.
 * @param {string} plaintext - The plain text to be encrypted.
 * @returns {string} A promise that resolves with the encrypted text.
 */
function encrypt(key, plaintext) {
  return CryptoJS.AES.encrypt(plaintext, key).toString();
}

/**
 * Decrypts the given encrypted text with the provided key using AES-256-GCM algorithm.
 * @param {string} key - The encryption key to use.
 * @param {string} encrypted - The encrypted text to be decrypted.
 * @returns {string} A promise that resolves with the decrypted plain text.
 */
function decrypt(key, encrypted) {
  return CryptoJS.AES.decrypt(encrypted, key).toString(CryptoJS.enc.Utf8);
}

/**
 * Wrapper class to convert Shopify order metadata into a time comparable order
 */
class ESimOrder {
  constructor(createdDate, esim, handle, lineItems) {
    this.createdDate = createdDate;
    this.iccid = esim.iccid;
    this.bundle = esim.bundle;
    this.token = esim.token;
    this.ref = esim.ref;
    this.handle = handle;
    if (lineItems && lineItems.length > 0) {
      this.productName = lineItems[0].title;
    } else {
      this.productName = "";
    }
  }
}

/**
* Wrapper class to give convenience methods for Mobile Data
*/
class MobileData {
  constructor(data) {
    this.data = data;
  }

  bytes() {
    return this.data;
  }

  kilobytes() {
    if (this.data != window.esimCardStrings.unlimitedBundle) {
      return this.data / 1000;
    }
    return this.data;
  }

  megabytes() {
    if (this.data != window.esimCardStrings.unlimitedBundle) {
      return this.data / 1000000;
    }
    return this.data;
  }

  gigabytes() {
    if (this.data != window.esimCardStrings.unlimitedBundle) {
      return this.data / 1000000000;
    }
    return this.data;
  }
}

/**
 * enum for Possible states of a Bundle
 */
const BundleState = Object.freeze({
  InstalledWithBundle: Symbol("InstalledWithBundle"),
  InstalledWithNoBundle: Symbol("InstalledWithNoBundle"),
  InstalledWithBundleQueued: Symbol("InstalledWithBundleQueued"),
  NotInstalledWithBundle: Symbol("NotInstalledWithBundle"),
  NotInstalledWithNoBundle: Symbol("NotInstalledWithNoBundle"),
});

const INSTALLED = "Installed";

/**
 *  Check customer orders, check for duplicate ICCIDs (in event of topups),
 *  Return a list of the most recent ICCID+Token combo
 *  The checking on order dates may be redundant due to ordering of shopify orders
 *  but there is no harm in checking.
 * @param {[ESimOrder]} orders           A list of ESimOrder.
 * @return {[ESimOrder]}                 A list of unique ESimOrders, with the most recent token.
 */
function getUniqueEsimsFromCustomerOrders(orders) {
  // Map with ICCID as key and and the most recent order for it
  let mostRecentUniqueOrders = new Map();

  // compare against current value held against iccid key for mostRecentUniqueOrders
  // if the order date is newer, replace the entry in map
  orders.forEach((esim) => {
    // check if iccid already exists in order map
    if (mostRecentUniqueOrders.has(esim.iccid)) {
      var prevOrder = mostRecentUniqueOrders.get(esim.iccid);
      var prevDate = prevOrder.createdDate;
      prevOrder.originalProductName = esim.productName;
      
      // check if order being proccessed is newer than the currently held value
      if (esim.createdDate > prevDate) {
        mostRecentUniqueOrders.set(esim.iccid, {...esim, originalProductName});
        return;
      }
    } else {
      // new unique iccid
      mostRecentUniqueOrders.set(esim.iccid, esim);
    }
  });

  // return as an array of ESimOrder
  return [...mostRecentUniqueOrders].map(([_, value]) => value);
}

/**
 *
 * GET Bundles assigned to an eSim using the `/esims/customer/$ref` endpoint
 *
 * `https://staging-api.esim-go.com/v2.3/esims/customer/$ref`
 *
 * @param {ESimOrder} esim         An eSim
 * @param {string} dataKey         The data key used to decrypt the customerRef
 * @return {JSON}                  Response from eSimGo API
 */
async function getEsimAssignmentData(esim, dataKey) {
  const url =
    ESIMGO_API_URL +
    API_VERSION +
    ESIMS_PATHS.Esims +
    ESIMS_PATHS.Customers +
    "/" +
    esim.ref +
    TOKEN_PARAM +
    esim.token;

  try {
    const customerPromise = fetchWithRetry(url).then(res => res.json());

    const helpersAppName = IS_PROD ? "esimgo-helpers" : "esimgo-helpers-develop";

    const bundlesUrl = `/apps/${helpersAppName}/app/esim-bundles/${esim.iccid}?includeUsed=true`;
    const bundlesPromise = fetchWithRetry(bundlesUrl).then(res => res.json()).catch(() => null);

    const [customerData, bundlesData] = await Promise.all([customerPromise, bundlesPromise]);

    if (customerData.message) {
      return customerData;
    }

    if (customerData.customerRef && dataKey && dataKey.length === 36) {
      customerData.customerRef = decrypt(dataKey, customerData.customerRef);
    } else {
      customerData.customerRef = "";
    }

    if (bundlesData?.bundles) {
      customerData.bundles = bundlesData.bundles;
    }

    if(esim.handle){
      customerData.handle = esim.handle
    }

    return customerData;
  } catch (err) {
    console.error("Error fetching esim assignment data:", err);
    return { message: "Error fetching data" };
  }
}

/**
 * Transforms sync endpoint assignments into legacy bundle shape for getBundlesByStatus().
 */
function transformSyncAssignments(syncEsim) {
  const assignments = syncEsim.assignments || [];
  const grouped = {};

  assignments.forEach((a) => {
    const key = a.bundleSku || "unknown";
    if (!grouped[key]) {
      grouped[key] = [];
    }

    const status = (a.status || "").toUpperCase();
    let startTime = a.createdAt || null;
    let bundleState = "active";

    if (status === "QUEUED") {
      startTime = null;
      bundleState = "queued";
    } else if (status === "EXHAUSTED") {
      bundleState = "expired";
    }

    grouped[key].push({
      startTime: startTime,
      endTime: a.expiresAt || null,
      initialQuantity: a.initialDataBytes != null ? parseInt(a.initialDataBytes, 10) : null,
      remainingQuantity: status === "EXHAUSTED" ? 0 : parseInt(a.remainingDataBytes || "0", 10),
      unlimited: a.initialDataBytes == null,
      bundleState: bundleState,
      assignmentDateTime: a.createdAt || null,
      bundleName: key,
    });
  });

  return Object.keys(grouped).map((key) => ({
    name: key,
    description: key,
    assignments: grouped[key],
  }));
}

/**
 * Transforms a raw sync-endpoint eSIM object into an ESimInformation
 * instance, resolving its display name/handle against the Shopify product
 * catalog. Shared by fetchNewSystemEsims (bulk) and fetchSingleNewSystemEsim
 * (single ICCID) so the two stay in sync.
 */
function buildEsimInformationFromSync(syncEsim, products) {
  const bundles = transformSyncAssignments(syncEsim);
  const profileStatus = syncEsim.firstInstallDate ? "Installed" : "Not Installed";

  const assignmentData = {
    iccid: syncEsim.iccid,
    bundles: bundles,
    profileStatus: profileStatus,
    shopifyOrderId: syncEsim.orders?.[0]?.orderId || null,
    notifications: {
      email: syncEsim.emailNotifications ?? false,
      sms: syncEsim.smsNotifications ?? false,
    },
    settings: {
      matchingId: syncEsim.matchingId,
      smdpAddress: syncEsim.smdpAddress,
    },
    customerRef: syncEsim.name || "",
    handle: "",
  };

  const bundleSku = syncEsim.assignments?.[0]?.bundleSku || "";
  let productName = "";
  if (products && bundleSku && products[bundleSku]) {
    productName = (products[bundleSku].product_title || "").replace(/ Default Title$/, "").trim();
    assignmentData.handle = products[bundleSku].parent_product || "";
  }

  const esimData = {
    bundle: bundleSku,
    productName: productName,
    originalProductName: null,
    createdDate: syncEsim.createdAt || "",
    ref: null,
    token: null,
  };

  return new ESimInformation(assignmentData, esimData, false);
}

/**
 * Fetches eSIMs from the new system using customer ID.
 * Returns { esims: [], failedIccids: [] } on any error (graceful fallback to legacy-only).
 * failedIccids lists ICCIDs whose sync call failed even after retries, so the
 * caller can surface "N eSIMs couldn't be loaded" instead of silently shrinking the count.
 */
async function fetchNewSystemEsims(products) {
  const failedIccids = [];
  try {
    const customerId = window.shopifySettings?.customerId;
    if (!customerId) return { esims: [], failedIccids };

    const helpersAppName = window.shopifySettings.usingProd
      ? "esimgo-helpers" : "esimgo-helpers-develop";

    // Step 1: List — discover eSIM ICCIDs
    const listUrl = `/apps/${helpersAppName}/app/customer-esims?logged_in_customer_id=${customerId}`;
    const listResponse = await fetchWithRetry(listUrl);
    const { esims } = await listResponse.json();
    if (!esims?.length) return { esims: [], failedIccids };

    // Step 2: Sync — fetch fresh data for each ICCID, batched to avoid rate limits
    // (same limit as the legacy path below, which hits the same eSIM-Go infrastructure)
    const syncUrl = `/apps/${helpersAppName}/api/customer-esim-sync?logged_in_customer_id=${customerId}`;
    const BATCH_SIZE = 5;
    const syncedEsims = [];
    for (let i = 0; i < esims.length; i += BATCH_SIZE) {
      const batch = esims.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.all(batch.map(async (esim) => {
        try {
          const res = await fetchWithRetry(syncUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ iccid: esim.iccid }),
          });
          const data = await res.json();
          return data.esim;
        } catch (err) {
          console.error(`Error syncing eSIM ${esim.iccid}:`, err);
          failedIccids.push(esim.iccid);
          return null;
        }
      }));
      syncedEsims.push(...batchResults);
    }

    // Step 3: Transform each synced eSIM into ESimInformation
    const esimInfos = syncedEsims.filter(Boolean).map((syncEsim) => buildEsimInformationFromSync(syncEsim, products));
    return { esims: esimInfos, failedIccids };
  } catch (err) {
    console.error("Error fetching new system eSIMs:", err);
    return { esims: [], failedIccids };
  }
}

/**
 * Fetches and syncs a single ICCID from the new system. Use this instead of
 * fetchNewSystemEsims() on pages that only need one eSIM (e.g. the eSIM
 * details page) — fetchNewSystemEsims lists and syncs the customer's entire
 * inventory, which is wasted work when only one ICCID is needed.
 * Returns null on any error or if the ICCID isn't found in the new system.
 */
async function fetchSingleNewSystemEsim(iccid, products) {
  try {
    const syncEsim = await syncSingleEsim(iccid);
    if (!syncEsim) return null;
    return buildEsimInformationFromSync(syncEsim, products);
  } catch (err) {
    console.error("Error fetching single new system eSIM:", err);
    return null;
  }
}

/**
 * Strips the vendor-prefix segment off an eSIM-Go bundle code, e.g.
 * "esimd_1GB_7D_GB_V2" -> "1GB_7D_GB_V2". That prefix (esimp_, esimd_, ...)
 * has changed before without the rest of the code changing, so matching on
 * everything after it stays resilient to future renames. Falls back to the
 * raw value for anything that doesn't look like a "<prefix>_<rest>" code.
 * @param {string} sku
 * @return {string}
 */
function normalizeBundleKey(sku) {
  if (!sku) return sku;
  const parts = sku.split('_');
  return parts.length > 1 ? parts.slice(1).join('_') : sku;
}

/**
 * Builds a display title/coverage pair for an eSIM from catalog metafields
 * (single-country name, data amount, unlimited flag) keyed by SKU, instead of
 * from product/variant title text — which is what makes fixed-plan titles
 * inconsistently show their data amount and Unlimited titles grow as long as
 * a product's title/variant options happen to be named in Shopify admin.
 *
 * Falls back to `{ title: null, ... }` for anything this can't confidently
 * resolve (SKU missing from the catalog, a multi-country/regional product
 * with no single country metafield, or no data-amount/unlimited signal on the
 * variant — e.g. travel pass), so callers can fall back to the existing
 * title-string logic unchanged.
 * @param {string} sku
 * @param {Object} products - the `custom_products` catalog map, keyed by the
 *   normalized bundle signature (see `normalizeBundleKey`)
 * @return 
 */
function resolveEsimTitleFields(sku, products) {
  const matchKey = normalizeBundleKey(sku);
  const entry = products && matchKey ? products[matchKey] : null;
  if (!entry) return { title: null, coverage: null, dataLabel: null, duration: null };

  const fallbackTitle = (entry.product_title || "").replace(/ Default Title$/, "").trim();
  const coverage = entry.country_name || stripDataInfoFromProductName(fallbackTitle);

  let dataLabel = null;
  if (entry.unlimited) {
    dataLabel = window.esimCardStrings.unlimitedBundle;
  } else if (entry.data_amount_mb) {
    const gb = entry.data_amount_mb / 1000;
    const gbLabel = gb % 1 === 0 ? gb : gb.toFixed(2);
    dataLabel = `${gbLabel}${window.esimCardStrings.gigabyte}`;
  }

  const duration = entry.duration_days || null;

  if (!coverage || !dataLabel) return { title: null, coverage: coverage || null, dataLabel, duration };

  return { title: `${coverage} ${dataLabel}`, coverage, dataLabel, duration };
}

/**
 * Merges legacy and new eSIMs by ICCID. New system wins on duplicates.
 */
function mergeEsimSystems(legacyEsims, newEsims) {
  const merged = new Map();

  legacyEsims.forEach((esim) => {
    if (esim) merged.set(esim.iccid, esim);
  });

  newEsims.forEach((esim) => {
    if (!esim) return;
    const legacy = merged.get(esim.iccid);
    if (legacy) {
      // Carry over Shopify catalog data from legacy when new system lacks it
      if (!esim.handle && legacy.handle) esim.handle = legacy.handle;
      if (!esim.productName && legacy.productName) esim.productName = legacy.productName;
      if (!esim.originalProductName && legacy.originalProductName) esim.originalProductName = legacy.originalProductName;
    }
    merged.set(esim.iccid, esim);
  });

  return [...merged.values()];
}

/**
 * Updates preferences for new-system eSIMs via the helpers app.
 */
async function updateEsimPreferencesNew(iccid, fields) {
  const customerId = window.shopifySettings?.customerId;
  if (!customerId) {
    console.error("updateEsimPreferencesNew: no customerId");
    return;
  }

  const helpersAppName = window.shopifySettings.usingProd
    ? "esimgo-helpers" : "esimgo-helpers-develop";

  const url = `/apps/${helpersAppName}/api/customer-esim-preferences?logged_in_customer_id=${customerId}`;

  try {
    const res = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ iccid, ...fields }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(`updateEsimPreferencesNew failed: ${res.status}`, text);
    }
  } catch (err) {
    console.error("Error updating new eSIM preferences:", err);
  }
}

/**
 * Syncs a single eSIM by ICCID via the helpers app sync endpoint.
 * Used by the install wizard to check install status for new-system eSIMs.
 */
async function syncSingleEsim(iccid) {
  const customerId = window.shopifySettings?.customerId;
  if (!customerId) return null;

  const helpersAppName = window.shopifySettings.usingProd
    ? "esimgo-helpers" : "esimgo-helpers-develop";
  const syncUrl = `/apps/${helpersAppName}/api/customer-esim-sync?logged_in_customer_id=${customerId}`;

  try {
    const res = await fetchWithRetry(syncUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ iccid }),
    });
    const data = await res.json();
    return data.esim;
  } catch (err) {
    console.error("Error syncing single eSIM:", err);
    return null;
  }
}

/**
 * Checks whether an ICCID is expired (deactivated after 6 months without
 * usage) via the helpers app's /esim-status/{iccid} endpoint.
 * Fails open (returns false) on any error, so a transient failure doesn't
 * incorrectly mark a working eSIM as expired.
 */
async function isEsimExpired(iccid) {
  const helpersAppName = window.shopifySettings?.usingProd
    ? "esimgo-helpers" : "esimgo-helpers-develop";
  const url = `/apps/${helpersAppName}/app/esim-status/${iccid}`;

  try {
    const res = await fetch(url);
    if (!res.ok) return false;
    const data = await res.json();
    return data.profileStatus === 'Deactivated';
  } catch (err) {
    console.error("Error checking eSIM expiry status:", err);
    return false;
  }
}

/**
* Esim Assignment Data returned from the eSimGo API
*/
class ESimInformation {
  constructor(esim_assignment_data, esim_data, isLegacy = true) {
    this.iccid = esim_assignment_data.iccid;
    this.bundle = getBundlesByStatus(esim_assignment_data.bundles);
    this.rawBundles = esim_assignment_data.bundles;
    this.profileStatus = esim_assignment_data.profileStatus;
    this.shopifyOrderId = esim_assignment_data.shopifyOrderId;
    this.notifications = esim_assignment_data.notifications;
    this.settings = esim_assignment_data.settings;
    this.customerRef = esim_assignment_data.customerRef;
    this.handle = esim_assignment_data.handle;
    this.currentBundle = esim_data.bundle;
    this.productName = esim_data.productName;
    this.originalProductName = esim_data.originalProductName;
    this.createdDate = esim_data.createdDate;
    this.ref = esim_data.ref;
    this.token = esim_data.token;
    this.isLegacy = isLegacy;
  }

  expirationDate() {
    if (this.bundle.active.length == 0) {
      return new Date(0);
    } else {
      return this.bundle.active[0].assignment.endTime;
    }
  }

  remainingActiveData() {
    let activeBundles = this.bundle.active.length;
    if (activeBundles == 0) {
      return new MobileData(0);
    } else {
      if (activeBundles > 1 ) {
        for (const activeBundle of this.bundle.active) {
          if (activeBundle.assignment.unlimited == false) {
            return new MobileData(activeBundle.assignment.remainingQuantity);
          }
        }
      } else {
        if (this.bundle.active[0].assignment.unlimited) {
          return new MobileData(window.esimCardStrings.unlimitedBundle);
        } else {
          return new MobileData(this.bundle.active[0].assignment.remainingQuantity);
        }
      }
    }
  }

  remainingQueuedData() {
    if (this.bundle.queued.length == 0) {
      return new MobileData(0);
    } else {
      if (this.bundle.queued[0].assignment.unlimited) {
        return new MobileData(window.esimCardStrings.unlimitedBundle);
      } else {
        return new MobileData(this.bundle.queued[0].assignment.remainingQuantity);
      }
    }
  }

  qrUrl() {
    if (this.isLegacy === false) {
      return `https://cdn.breezesim.com/qr-codes/${this.iccid}.png`;
    }
    return `${ESIMGO_API_URL}${API_VERSION}/esims/qr/${this.ref}?token=${this.token}`;
  }
}

/**
 * Builds a minimal ESimInformation for a legacy eSIM whose live assignment
 * data couldn't be fetched from eSIM-Go (e.g. it has nothing left to return
 * for a long-deactivated profile). Used as a fallback so the eSIM still
 * renders a card — with no bundle/usage data, since none is available —
 * instead of silently dropping into the "failed to load" bucket. The caller
 * is expected to have already confirmed the ICCID via isEsimExpired().
 * @param {ESimOrder} esim
 * @return {ESimInformation}
 */
function buildExpiredLegacyEsimInfo(esim) {
  const assignmentData = {
    iccid: esim.iccid,
    bundles: [],
    profileStatus: "Not Installed",
    shopifyOrderId: null,
    notifications: { email: false, sms: false },
    settings: {},
    customerRef: "",
    handle: esim.handle || "",
  };

  return new ESimInformation(assignmentData, esim);
}

function getBundlesByStatus(esimBundles) {
  let activeBundles = [];
  let queuedBundles = [];
  let expiredBundles = [];
  let travelpassBundles = [];
  let allBundles = [];

  for (let b = 0; b < esimBundles.length; b++) {
    let currentBundle = JSON.parse(JSON.stringify(esimBundles[b]));
    delete currentBundle.assignments;

    for (let ba = 0; ba < esimBundles[b].assignments.length; ba++) {
      let bundleAssignment = JSON.parse(JSON.stringify(currentBundle));
      let assignment = esimBundles[b].assignments[ba];
      bundleAssignment.assignment = assignment;
      let startTime = assignment.startTime
        ? new Date(assignment.startTime)
        : null;
      let endTime = assignment.endTime ? new Date(assignment.endTime) : null;

      // All bundles
      allBundles.push(bundleAssignment);

      // if bundle is travelpass keep original information
      if(currentBundle.name.includes('travelpass') && currentBundle.name.includes('x12')){
        travelpassBundles.push(bundleAssignment);
      }

      // if bundle is not started and not known to be exhausted, it's queued.
      if (startTime == null && assignment.bundleState !== "expired") {
        bundleAssignment.assignment.bundleState = "queued";
        queuedBundles.push(bundleAssignment);
        continue;
      }

      // if endTime is past now, bundle is expired.
      if (endTime < Date.now()) {
        bundleAssignment.assignment.bundleState = "expired";
        expiredBundles.push(bundleAssignment);
        continue;
      }

      // check to ensure Bundle is not completed, if it is then we ignore it.
      // unless its unlimited, as remaining quantity will show remaining unthrottled data
      if (assignment.remainingQuantity > 0 || assignment.unlimited) {
        bundleAssignment.assignment.bundleState = "active";
        activeBundles.push(bundleAssignment);
        continue;
      }

      bundleAssignment.assignment.bundleState = "depleted";
      expiredBundles.push(bundleAssignment);
    }
  }

  return {
    active: activeBundles,
    queued: queuedBundles,
    expired: expiredBundles,
    travelPass: travelpassBundles,
    allBundles: allBundles
  };
}

/**
 *
 * UPDATE the `customerRef` of an eSim using the `/esims/customers` endpoint
 *
 * `https://staging-api.esim-go.com/v2.3/esims/customers/$ref`
 *
 * The `customerRef` is used as a method of allowing the end user to name their eSim.
 * @param {ESimOrder} esim         An eSim
 * @param {String} customerRef     The new name of the eSim
 * @param {String} dataKey         The data key used to encrypt the customerRef
 * @return {JSON}                  Response from eSimGo API
 */
async function updateEsimCustomerReference(esim, customerRef, dataKey) {
  if (!dataKey || dataKey.length !== 36) {
    return;
  }

  let url =
    ESIMGO_API_URL +
    API_VERSION +
    ESIMS_PATHS.Esims +
    ESIMS_PATHS.Customers +
    "/" +
    esim.ref +
    TOKEN_PARAM +
    esim.token;
  return fetch(url, {
    method: "PUT",
    body: JSON.stringify({
      newName: encrypt(dataKey, customerRef),
    }),
  }).catch((err) => console.error(err));
}

/**
 *
 * UPDATE the Notification preferences of an eSim using the `/esims/customers` endpoint
 *
 * `https://staging-api.esim-go.com/v2.3/esims/customers/$ref`
 *
 * @param {ESimOrder} esim                          An eSim
 * @param {Boolean} emailNotificationsEnabled       Allow email notifications
 * @param {Boolean} smsNotificationsEnabled         Allow SMS notifications
 * @param {String} ref                              The eSIM reference
 * @param {String} token                            The eSIM token
 * @return {JSON}                                   Response from eSimGo API
 */
async function updateNotificationsPreferences(
  emailNotificationsEnabled,
  smsNotificationsEnabled,
  ref,
  token
) {
  const helpers_app_name = IS_PROD ? "esimgo-helpers" : "esimgo-helpers-develop";
  const url = `/apps/${helpers_app_name}/api/customer-notifications`;

  return fetch(url, {
    method: "PUT",
    body: JSON.stringify({
      emailNotificationsEnabled: emailNotificationsEnabled.toString(),
      smsNotificationsEnabled: smsNotificationsEnabled.toString(),
      ref: ref,
      token: token
    }),
  }).catch((err) => console.error(err));
}

/* Zendesk Functions */
async function getZendeskLocale(currentLocale) {
  let locale = "en-gb";

  if (currentLocale) {
    currentLocale = currentLocale.toLowerCase();

    let url = ZENDESK_URL + "/api/v2/help_center/locales";

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          if (res.locales && res.locales.includes(currentLocale)) {
            return currentLocale;
          } else if (res.locales.length > 0) {
            for (let i = 0; i < res.locales.length; i++) {
              if (
                res.locales[i].substring(0, 2) == currentLocale.substring(0, 2)
              ) {
                return res.locales[i];
              }
            }
          }
          return locale;
        })
        .catch((err) => {
          return locale;
        })
    );
  }

  return locale;
}

async function getZendeskSections(locale, categoryId) {
  if (locale && categoryId) {
    let url =
      ZENDESK_URL +
      "/api/v2/help_center/" +
      locale +
      "/categories/" +
      categoryId +
      "/sections";

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          return res;
        })
        .catch((err) => {
          return err;
        })
    );
  }

  return false;
}

async function getZendeskArticlesBySection(locale, sectionId) {
  if (locale && sectionId) {
    let url =
      ZENDESK_URL +
      "/api/v2/help_center/" +
      locale +
      "/sections/" +
      sectionId +
      "/articles";

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          return res;
        })
        .catch((err) => {
          return err;
        })
    );
  }

  return false;
}

async function getZendeskArticlesByLabel(locale, sectionId, label) {
  if (locale && sectionId && label) {
    let url =
      ZENDESK_URL +
      "/api/v2/help_center/articles/search.json?section=" +
      sectionId +
      "&locale=" +
      locale +
      "&label_names=" +
      label;

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          return res;
        })
        .catch((err) => {
          return err;
        })
    );
  }

  return false;
}